// CORE
import { Map as ImmutableMap } from 'immutable';
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';

import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
  scheduledTransactionsTypes,
  scheduledTransactionsUtils,
} from 'app-components/flux/scheduled-transactions';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import type { TransactionProps, TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import { upcomingSelectors } from 'app-components/flux/upcoming';

// MUI
import CloseRounded from '@mui/icons-material/CloseRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

import { rootUiActions, rootUiSelectors, rootUiTypes } from 'app-components/flux/root-ui';
import { numberUtils } from 'app-components/utils/number';
import { HandleEndOption } from 'components/BillPresentment/utils';

import { useAppSelector } from 'hooks/redux';
// Data
import { useDispatch } from 'react-redux';

import BillPresentmentConnectBox from 'components/BillPresentmentConnectBox';
import ConnectedAvatar from 'components/ConnectedAvatar';
// Custom
import { DIALOG_TYPE as DIALOG_CONFIRMATION, mkConfirmationDialogProps } from 'components/Dialogs/ConfirmationDialog';
import StdDialogTitle from 'components/Dialogs/StdDialogTitle';
import { QButtonVariantEnum } from 'components/QButton';
import { QDropdownMenu, QMenuDivider, QMenuItem } from 'components/QDropdowns';
import StandardDrawer, { useStandardDrawerVariant } from 'components/Drawers/StandardDrawer';
import {
  getRootModalRemoveDialogOptions,
  type RootModalDrawerOptions,
} from 'components/Drawers/standardDrawerContract';
import ScheduledTransactionEditDialog, {
  closeScheduledTransactionEditDialog,
  showConfirmationIfDirty,
  showScheduledTransactionEditDialog,
} from 'components/Dialogs/ScheduledTransactionEditDialog';
import SeriesBarChart from 'components/Charts/SeriesBarChart';
import SkeletonRow from 'components/Upcoming/skeleton';
import ViewRow from 'components/Upcoming/viewRow';
import { SeriesHistoryRow } from './SeriesHistoryRow';
import AlertDialog from '../AlertDialog';
import useFormatAmount from 'app-components/hooks/privacyMode/useFormatAmount';
import { showTxnDetailsDialog } from 'components/Dialogs/TransactionEditDialog/actions';
import useQDialogs from 'components/QDialogs/useQDialogs';

const useStyles = makeStyles()((theme) => ({
  root: {
    minHeight: 284,
    overflow: 'hidden',
  },
  dialogBackground: {
    background: theme.palette.background.paper,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  canceledChip: {
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.layer1,
    padding: '2px 4px',
    borderRadius: 6,
  },

  // header + title stuff
  titleRow: {
    top: 0,
    flexShrink: 0,
    position: 'sticky',
    padding: '24px 24px 24px',
    marginBottom: 4,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    backgroundColor: 'inherit',
    borderBottom: `1px solid ${theme.palette.border.light}`,
    zIndex: 1,
    '&::after': {
      content: "''",
      position: 'absolute',
      bottom: -8,
      left: 0,
      height: 8,
      width: '100%',
      backgroundImage: 'unset',
    },
  },
  drawerTitleRow: {
    position: 'relative',
    padding: '16px 24px 0',
    marginBottom: 0,
    alignItems: 'center',
    borderBottom: 0,
    '&::after': {
      content: 'none',
    },
  },
  nameColumn: {
    marginLeft: 24,
    flexGrow: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  buttonRow: {
    flexShrink: 0,
    display: 'flex',
    gap: theme.spacing(1),
  },
  // graph
  graphSection: {
    margin: '16px 12px',
    height: 208,
    flexShrink: 0,
  },

  centeredRow: {
    padding: '16px 24px',
    flexShrink: 0,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
    minWidth: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    width: `calc(100% + ${theme.spacing(3)})`,
    paddingRight: theme.spacing(3),
    marginRight: `-${theme.spacing(3)}`,
  },
  comingUp: {
    padding: 24,
    borderRadius: 12,
    margin: '0 16px',
    backgroundColor: theme.palette.background.default,
  },

  lowerSection: {
    width: '100%',
    padding: '32px 24px 24px',
  },

  zeroState: {
    margin: '36px 48px',
  },
}));

const location = 'view series';

type ScheduledTransactionEditDialogProps = React.ComponentProps<typeof ScheduledTransactionEditDialog>;

type EditSeriesOverlayDrawerProps = Omit<
  ScheduledTransactionEditDialogProps,
  'onBack' | 'onClose' | 'open' | 'rootModalDrawer'
> & {
  onBack?: ScheduledTransactionEditDialogProps['onBack'];
  onClose: ScheduledTransactionEditDialogProps['onClose'];
  open: boolean;
  rootModalDrawer: RootModalDrawerOptions;
};

function EditSeriesOverlayDrawer({
  onBack,
  onClose,
  open,
  rootModalDrawer,
  ...dialogProps
}: EditSeriesOverlayDrawerProps): React.JSX.Element | null {
  const [pendingCloseAction, setPendingCloseAction] = useState<null | (() => void)>(null);

  React.useEffect(() => {
    if (open) {
      setPendingCloseAction(null);
    }
  }, [open]);

  const animateThen = useCallback((action: () => void): void => {
    setPendingCloseAction(() => action);
  }, []);

  const handleClose = useCallback<ScheduledTransactionEditDialogProps['onClose']>(
    (isDirty, forceClose) => {
      animateThen(() => onClose(isDirty, forceClose));
    },
    [animateThen, onClose],
  );

  const handleBack = useMemo(() => (onBack ? () => animateThen(onBack) : undefined), [animateThen, onBack]);

  const handleExited = useCallback((): void => {
    const action = pendingCloseAction;
    setPendingCloseAction(null);
    action?.();
  }, [pendingCloseAction]);

  const drawerOpen = open && !pendingCloseAction;

  return (
    <StandardDrawer
      closeTransition={rootModalDrawer.closeTransition}
      variant='overlay'
      onClose={() => handleClose()}
      onExited={handleExited}
      open={drawerOpen}
      showScrim={false}
      width={rootModalDrawer.width ?? 'Medium'}
    >
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <ScheduledTransactionEditDialog
          {...dialogProps}
          onBack={handleBack}
          onClose={handleClose}
          open
          rootModalDrawer={rootModalDrawer}
        />
      </Box>
    </StandardDrawer>
  );
}

const ViewSeriesDialog = (props: {
  dialogId?: string;
  rootModalDrawer?: RootModalDrawerOptions;
  txn?: TransactionProps;
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { dialogAlert } = useQDialogs();
  const isOverlayDrawer = useStandardDrawerVariant() === 'overlay';

  const { stModelId } = props.txn || {};

  const model = useAppSelector((state) =>
    scheduledTransactionsSelectors.getScheduledTransactionById(state, stModelId || ''),
  );
  const transaction = model?.transaction || props.txn || ({} as TransactionProps);
  const { history, oldestPending: txn } = useAppSelector((state) =>
    upcomingSelectors.getModelHistory(state, model),
  ) as {
    history: ImmutableMap<string, TransactionRecord>;
    oldestPending?: TransactionProps;
  };
  const activeModals = useAppSelector(rootUiSelectors.getActiveModalsAsJS) as Array<{ id?: string }>;

  const displayName = scheduledTransactionsUtils?.getDisplayName?.(transaction) || '';

  const isIncome = useAppSelector((state) => {
    if (model?.transaction) {
      return scheduledTransactionsSelectors.isIncomeSeriesTxn(state, model.transaction);
    }
    return transactionsSelectors.isIncomeTxn(state, transaction);
  });
  const splitSeriesFf = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'splitSeries'));
  let color = isIncome ? theme.palette.legend.income : theme.palette.legend.bill;
  if (model?.isCompleted) color = theme.palette.text.secondary;
  const { classes, cx } = useStyles();
  const showDrawerBackButton = Boolean(props.rootModalDrawer && isOverlayDrawer);

  const negator =
    (transactionsUtils.isTransferTxn(transaction) && Math.sign(transaction.amount)) || (isIncome ? 1 : -1);
  const { chartData, average, sum } = upcomingSelectors.generateChartData(history, negator);

  const frequency = model && scheduledTransactionsUtils.frequencyAliasFromRecurrence(model.recurrence);
  const drawerTitle = model ? 'View Series' : 'View One-time payment';

  // +++     State values     +++
  const [editSeries, setEditSeries] = useState<boolean | number>(false);
  const [openState, setOpenState] = useState(true);
  const [dirtyDialog, setDirtyDialog] = useState(false);

  const closeOptionsMenuThen = useCallback((callback: () => void): void => {
    window.setTimeout(() => {
      callback();
    }, 0);
  }, []);

  const onClose = useCallback(() => {
    setOpenState(false);
    dispatch(rootUiActions.removeDialog(props.dialogId, getRootModalRemoveDialogOptions(props.rootModalDrawer)));
  }, [setOpenState, dispatch, props.dialogId, props.rootModalDrawer]);

  // The scheduled transaction is removed from the store synchronously on delete (see
  // doUpsertResource), which unmounts EditSeriesOverlayDrawer (gated on `model`) before its
  // animated close can call back into this dialog. Watch for that disappearance directly so
  // deleting the series always closes this view instead of showing the "no model" zero state.
  const hadModelRef = useRef(Boolean(model));
  useEffect(() => {
    if (hadModelRef.current && !model) {
      setEditSeries(false);
      onClose();
    }
    hadModelRef.current = Boolean(model);
  }, [model, onClose]);

  const openOrRevealTxnDetails = useCallback(
    (clickedTxn: TransactionRecord) => {
      const existingTxnDetailsDialogId = `TRANSACTION_DETAILS_${clickedTxn?.id || '0'}`;
      const txnDetailsAlreadyOpen = activeModals.some((modal) => modal.id === existingTxnDetailsDialogId);

      if (txnDetailsAlreadyOpen) {
        onClose();
        return;
      }

      dispatch(showTxnDetailsDialog({ txn: clickedTxn, wrapperId: 'view-series' }));
    },
    [activeModals, dispatch, onClose],
  );

  const historyDisplay = history
    .filter((historyTxn) => historyTxn.source !== 'SCHEDULED_TRANSACTION_PENDING')
    .map((historyTxn) => {
      return splitSeriesFf ? (
        <React.Fragment key={historyTxn.id}>
          <SeriesHistoryRow
            txn={historyTxn}
            isIncome={isIncome}
            baseId={`series-history-${historyTxn.id}`}
            onClick={openOrRevealTxnDetails}
            rootModalDrawer={props.rootModalDrawer}
          />
        </React.Fragment>
      ) : (
        <React.Fragment key={historyTxn.id}>
          <ViewRow
            txn={historyTxn as TransactionRecord}
            isIncome={isIncome}
            baseId={`series-history-${historyTxn.id}`}
            past
          />
        </React.Fragment>
      );
    })
    .toIndexedSeq();

  const closeEditDialog = useCallback(
    (isDirty?: boolean, forceClose?: boolean) => {
      if (forceClose) {
        dispatch(closeScheduledTransactionEditDialog());
        onClose();
      }
      if (isDirty) {
        setDirtyDialog(true);
      } else {
        dispatch(closeScheduledTransactionEditDialog());
      }
    },
    [dispatch, onClose],
  );

  const openEditSeries = useCallback(
    (initialTab = 0) => {
      if (props.rootModalDrawer) {
        setEditSeries(initialTab);
        return;
      }

      model &&
        dispatch(
          showScheduledTransactionEditDialog({
            open: Boolean(model),
            scheduledTransaction: model,
            initialTab,
            onClose: showConfirmationIfDirty(dialogAlert, () => {
              dispatch(closeScheduledTransactionEditDialog());
            }),
          }),
        );
    },
    [dialogAlert, dispatch, model, props.rootModalDrawer],
  );

  const closeLocalEditSeries = useCallback<ScheduledTransactionEditDialogProps['onClose']>(
    (isDirty, forceClose) => {
      if (forceClose) {
        setEditSeries(false);
        onClose();
        return;
      }
      showConfirmationIfDirty(dialogAlert, () => {
        setEditSeries(false);
      })(Boolean(isDirty), Boolean(forceClose));
    },
    [dialogAlert, onClose],
  );

  const closeLocalEditSeriesWithoutConfirmation = useCallback((): void => {
    setEditSeries(false);
  }, []);

  const openEndSeries = useCallback(() => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'end-series-confirmation',
          type: DIALOG_CONFIRMATION,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'End series',
              content: (
                <Typography variant='bodyRegular' id='confirmation-dialog-description'>
                  Ending your&nbsp;<b>{displayName || 'this transaction'}</b>&nbsp;series will only mark it as ended. It
                  will not delete your series or any history related to it.
                </Typography>
              ),
              confirmLabel: 'End Series',
              allowNesting: true,
              confirmAction: (_reason, _closeEvent) => {
                HandleEndOption(dispatch, model, location, true);
              },
              denyLabel: 'Cancel',
            }),
          ),
        }),
      ),
    );
  }, [dispatch, displayName, model]);

  const openDeleteSeries = useCallback(() => {
    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'delete-series-confirmation',
          type: DIALOG_CONFIRMATION,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Delete recurring series',
              content: `Deleting the recurring series for ${displayName} will delete all future reminders for this series.`,
              confirmLabel: 'Delete',
              confirmButtonVariant: QButtonVariantEnum.DELETE,
              allowNesting: true,
              confirmAction: (_reason, _closeEvent) => {
                if (stModelId) {
                  dispatch(
                    scheduledTransactionsActions.deleteScheduledTransaction(
                      scheduledTransactionsTypes.mkScheduledTransactionPatch({
                        id: stModelId,
                        isUserVerified: true,
                        isDeleted: true,
                      }),
                      { undo: { userMessage: 'Recurring transaction deleted.' } },
                    ),
                  );
                }
                closeEditDialog(false, true);
              },
              denyLabel: 'Cancel',
              denyButtonVariant: QButtonVariantEnum.DEFAULT,
            }),
          ),
        }),
      ),
    );
  }, [dispatch, stModelId, displayName, closeEditDialog]);

  const handleEditSeriesOptionSelect = useCallback((): void => {
    closeOptionsMenuThen(() => {
      openEditSeries();
    });
  }, [closeOptionsMenuThen, openEditSeries]);

  const handleDeleteSeriesOptionSelect = useCallback((): void => {
    closeOptionsMenuThen(openDeleteSeries);
  }, [closeOptionsMenuThen, openDeleteSeries]);

  const handleEndSeriesOptionSelect = useCallback((): void => {
    closeOptionsMenuThen(openEndSeries);
  }, [closeOptionsMenuThen, openEndSeries]);

  const formatAmount = useFormatAmount();
  const showSeriesContent = openState;
  const renderSeriesContent = props.rootModalDrawer || openState;
  const renderCloseButton = (
    <IconButton size='small' variant='tonal' title='Close' id='dlg-close' onClick={onClose}>
      <CloseRounded />
    </IconButton>
  );
  const seriesContent = (
    <div className={classes.dialogBackground}>
      {props.rootModalDrawer && (
        <StdDialogTitle
          variant='drawer'
          id='dialog-title'
          onBack={showDrawerBackButton ? onClose : undefined}
          onClose={onClose}
          showBackButton={showDrawerBackButton}
          showCloseButton
        >
          {drawerTitle}
        </StdDialogTitle>
      )}
      <div
        sharedcomponentid='ViewSeriesDialog'
        className={cx(classes.titleRow, props.rootModalDrawer && classes.drawerTitleRow)}
      >
        <ConnectedAvatar seriesIcon model={model} txn={transaction} size={80} iconSize={32} color={color} editable />
        <div className={classes.nameColumn}>
          <Typography
            id='series-modal-title-text'
            variant='displayRegular'
            style={{ width: '100%', padding: '8px 0' }}
            noWrap
          >
            {displayName}
          </Typography>
          {model?.isCompleted ? (
            <Typography variant='paragraphRegular' className={classes.canceledChip}>
              Canceled
            </Typography>
          ) : (
            <Typography variant='bodyMedium' color={theme.palette.text.secondary}>
              {frequency}
            </Typography>
          )}
        </div>
        <div className={classes.buttonRow}>
          {model && (
            <QDropdownMenu
              id='series-options-btn'
              triggerVariant='button'
              buttonVariant='outlined'
              label='Manage series'
              width={320}
            >
              {model.isCompleted ? (
                <QMenuItem
                  variant='action'
                  id='delete-series-option'
                  label='Delete series'
                  subtext='Permanently removes the series & reminders'
                  destructive
                  onClick={handleDeleteSeriesOptionSelect}
                />
              ) : (
                [
                  <QMenuItem
                    key='edit'
                    variant='action'
                    id='edit-series-option'
                    label='Edit series'
                    startIcon={<EditRoundedIcon fontSize='small' />}
                    onClick={handleEditSeriesOptionSelect}
                  />,
                  <QMenuItem
                    key='delete'
                    variant='action'
                    id='delete-series-option'
                    label='Delete series'
                    subtext='Permanently removes the series & reminders'
                    destructive
                    startIcon={<DeleteRoundedIcon fontSize='small' />}
                    onClick={handleDeleteSeriesOptionSelect}
                  />,
                  <QMenuDivider key='divider' />,
                  <QMenuItem
                    key='end'
                    variant='action'
                    id='end-series-option'
                    label='End series'
                    subtext='Keeps history, stops future reminders'
                    startIcon={<StopRoundedIcon fontSize='small' />}
                    onClick={handleEndSeriesOptionSelect}
                  />,
                ]
              )}
            </QDropdownMenu>
          )}
          {!props.rootModalDrawer && renderCloseButton}
        </div>
      </div>
      <div className={classes.content}>
        {!model?.isCompleted && (
          <BillPresentmentConnectBox
            model={model}
            location={location}
            onConnect={onClose}
            onEdit={openEditSeries}
            rootModalDrawer={props.rootModalDrawer}
          />
        )}

        {model ? (
          <>
            <div className={classes.graphSection}>
              <SeriesBarChart
                colorOverride={model.isCompleted ? color : undefined}
                chartData={chartData}
                average={isIncome ? average : average * -1}
                isIncome={isIncome}
              />
            </div>

            <div className={classes.centeredRow}>
              <Typography variant='bodyRegular'>
                {`Total in Last 6 months : ${formatAmount(sum, undefined, '0,0', numberUtils.ShowSignEnum.POSITIVE_ONLY)}`}
              </Typography>
            </div>

            {!model.isCompleted && (
              <div className={classes.comingUp}>
                <Typography variant='titleMedium' style={{ paddingBottom: 8 }}>
                  Coming Up
                </Typography>
                {txn?.type ? (
                  <ViewRow
                    txn={txn as TransactionRecord}
                    isIncome={isIncome}
                    baseId='coming-soon'
                    rootModalDrawer={props.rootModalDrawer}
                  />
                ) : (
                  <SkeletonRow string2="You don't have anything coming up." />
                )}
              </div>
            )}

            <div className={classes.lowerSection}>
              <Typography variant='titleMedium'>{splitSeriesFf ? 'Linked Transactions' : 'History'}</Typography>

              {historyDisplay?.size ? (
                historyDisplay
              ) : (
                <SkeletonRow
                  string1={splitSeriesFf ? 'No linked transactions' : 'Blank Slate!'}
                  string2={splitSeriesFf ? 'Transactions linked to this series will show here.' : 'your past is clear'}
                />
              )}
            </div>
          </>
        ) : (
          <div className={classes.zeroState}>
            <SkeletonRow
              string1='One Time Bill!'
              string2="there's no recurring model associated with this transaction"
            />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {props.rootModalDrawer ? (
        renderSeriesContent ? (
          seriesContent
        ) : null
      ) : (
        <Dialog open={showSeriesContent} maxWidth='md' fullWidth classes={{ paper: classes.root }} onClose={onClose}>
          {seriesContent}
        </Dialog>
      )}

      {dirtyDialog && (
        <AlertDialog
          dialogId={'ignore this warning'}
          title='Just checking...'
          content='Are you sure you want to discard all changes?'
          onClose={(btnObj: any) => {
            if (btnObj.btnPressed === 'Discard') setEditSeries(false);
            setDirtyDialog(false);
          }}
          buttons={['Go Back', 'Discard']}
        />
      )}

      {props.rootModalDrawer && model && editSeries !== false && (
        <EditSeriesOverlayDrawer
          key={model.clientId || model.id || 'edit-series'}
          open
          rootModalDrawer={props.rootModalDrawer}
          scheduledTransaction={model}
          onClose={closeLocalEditSeries}
          onBack={closeLocalEditSeriesWithoutConfirmation}
          initialTab={typeof editSeries === 'number' ? editSeries : 0}
        />
      )}
    </>
  );
};

ViewSeriesDialog.displayName = 'ViewSeriesDialog';

export default ViewSeriesDialog;
